import {
  searchAssetsAndCollections,
  type AssetsAndCollectionsSearchResult,
} from "@core/lib/api/searchAssetsAndCollections";
import { useWire } from "@core/wire";
import { debounce } from "@solid-primitives/scheduled";
import type { Editor, Range } from "@tiptap/core";
import {
  ActionKind,
  type AutocompleteAction,
  type AutocompleteTrMeta,
  closeAutocomplete,
  pluginKey,
} from "prosemirror-autocomplete";
import { createStore } from "solid-js/store";
import { MENTION_INPUT_DATA_ATTR, MentionsExtension } from "./Mentions.extension";
import type { EditorView } from "@tiptap/pm/view";
import { startTransition } from "solid-js";

export const useMentionsState = () => {
  const wire = useWire();

  let editor: Editor | null = null;
  const [state, setState] = createStore({
    open: false,
    loading: false,
    data: [] as AssetsAndCollectionsSearchResult,
    activeIndex: 0,
    error: null as string | null,
    domAnchor: null as HTMLElement | null,
    lastFilter: "",
    view: null as EditorView | null,
    range: null as Range | null,
    lastInteraction: "keyboard" as "keyboard" | "mouse",
  });

  /**
   * Used for disabling mouse enter events when the user is interacting with the mentions picker via keyboard.
   * This prevents the mouse interfering with the keyboard navigation (especially when the items are scrolled).
   */
  const setLastInteraction = (interaction: "keyboard" | "mouse") => {
    setState("lastInteraction", interaction);
  };

  const getData = debounce(async (query?: string) => {
    await startTransition(async () => {
      try {
        const result = await searchAssetsAndCollections(query || "", wire);
        setState("data", result);
      } catch (error) {
        console.error(error);
        setState("error", "Something went wrong");
      }
      setState("loading", false);
    });
  }, 200);

  const retry = () => {
    setState({
      activeIndex: 0,
      open: true,
      loading: true,
      error: null,
    });
    getData(state.lastFilter);
  };

  const select = (itemIndex: number) => {
    if (!state.range || !state.view || !editor) return;
    const item = state.data[itemIndex];
    if (!item) return;

    const nodeType = editor.schema.nodes[MentionsExtension.name]!;
    const node = nodeType.create({
      type: item.type,
      associatedCollection: item.associatedCollection,
      data: { ...item.data },
    });
    const tr = state.view.state.tr.replaceRangeWith(state.range.from, state.range.to, node);
    state.view.dispatch(tr);
    editor.commands.focus();
  };

  const dismiss = () => {
    if (!state.view) return;
    closeAutocomplete(state.view);
    editor?.commands.focus();
  };

  const autocompleteActionsHandler = async (event: { action: AutocompleteAction; editor: Editor }) => {
    editor = event.editor;
    setState({
      view: event.action.view,
      range: event.action.range,
    });

    if (event.action.kind === ActionKind.close) {
      setState({ open: false, domAnchor: null, error: null, view: null, range: null });
      getData.clear();
      return;
    }

    if (event.action.kind === ActionKind.open) {
      setState({
        activeIndex: 0,
        open: true,
        loading: true,
        error: null,
        lastFilter: event.action.filter || "",
        domAnchor: event.action.view.dom.querySelector(`[${MENTION_INPUT_DATA_ATTR}]`) as HTMLElement | null,
      });
      getData(event.action.filter);
      return;
    }

    if (event.action.kind === ActionKind.filter) {
      setState({
        activeIndex: 0,
        loading: true,
        error: null,
        lastFilter: event.action.filter || "",
        domAnchor: event.action.view.dom.querySelector(`[${MENTION_INPUT_DATA_ATTR}]`) as HTMLElement | null,
      });
      getData(event.action.filter);
      return;
    }

    if (event.action.kind === ActionKind.up) {
      setLastInteraction("keyboard");
      if (!state.open || state.loading) return;

      const len = state.data.length;
      if (len === 0) return;

      const newIndex = state.activeIndex - 1;
      setState("activeIndex", newIndex < 0 ? len - 1 : newIndex);
      return;
    }

    if (event.action.kind === ActionKind.down) {
      setLastInteraction("keyboard");
      if (!state.open || state.loading) return;

      const len = state.data.length;
      if (len === 0) return;

      const newIndex = state.activeIndex + 1;
      setState("activeIndex", newIndex >= len ? 0 : newIndex);
      return;
    }
    if (event.action.kind === ActionKind.enter) {
      if (state.error) {
        retry();
        return;
      }
      if (state.data.length === 0) {
        dismiss();
        return;
      }
      select(state.activeIndex);
    }
  };

  const activateMention = (editor: Editor | null) => {
    if (!editor) return;
    const view = editor.view;
    const { $from, $to } = editor.state.selection;
    const hasSpaceBefore = $from.nodeBefore?.text?.slice(-1) === " " || !$from.nodeBefore;
    const contentToInsert = hasSpaceBefore ? "@" : " @";
    view.someProp("handleTextInput", (f) => f(view, $from.pos, $to.pos, contentToInsert));
    view.focus();
  };

  return {
    mentionsState: state,
    autocompleteActionsHandler,
    retry,
    select,
    dismiss,
    setLastInteraction,
    activateMention,
    setActiveIndex: (index: number) => setState("activeIndex", index),
  };
};
